import { Injectable } from '@angular/core';
import { FetchPolicy } from '@core/enums/fetch-policy-apollo.enum';
import {
  EstimateDeliveryDateChangeResponse,
  EstimateMultipleOrderTransferResponse,
  EstimateOrderTransferResponse,
  OrderFilterDto,
  OrderTransferEstimationResult,
  OrderTransferResult,
  TransferDeliveryDateChangeResponse,
  TransferMultipleOrdersToRouteListResponse,
  TransferOrderToRouteListResponse,
} from '@core/interfaces/change-perform.interface';
import { Apollo, gql, MutationResult } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import { OrderTransferParams, TransferMultipleOrdersEstimationDto } from '@core/interfaces/order-transfer.interface';
import { OrderListResponse } from '@core/interfaces/order-reference-guide.interface';
import { ApolloQueryResult } from '@apollo/client/core';
import { RouteSheetListGql, RouteSheetsFilter } from '@core/interfaces/route-sheets.interface';
import { GetListParams } from '@core/interfaces/get-list.interface';

@Injectable({
  providedIn: 'root',
})
export class ChangePerformerService {
  constructor(private apollo: Apollo) {}

  public estimateDeliveryDateChange(data: OrderTransferParams): Observable<OrderTransferEstimationResult> {
    return this.apollo
      .query<EstimateDeliveryDateChangeResponse>({
        query: gql`
          query EstimateOrderAssignmentOnRouteListAfterDeliveryDateChange($data: OrderTransferEstimationDto!) {
            estimateOrderAssignmentOnRouteListAfterDeliveryDateChange(data: $data) {
              success
              rejectionReason
            }
          }
        `,
        variables: { data },
        fetchPolicy: FetchPolicy.NoCache,
      })
      .pipe(map((res) => res.data.estimateOrderAssignmentOnRouteListAfterDeliveryDateChange));
  }

  public transferDeliveryDateChange(
    data: OrderTransferParams,
  ): Observable<MutationResult<TransferDeliveryDateChangeResponse>> {
    return this.apollo.mutate<TransferDeliveryDateChangeResponse>({
      mutation: gql`
        mutation AssignOrderOnRouteListAfterDeliveryDateChange($data: OrderTransferEstimationDto!) {
          assignOrderOnRouteListAfterDeliveryDateChange(data: $data) {
            success
            deliveryFrom
            deliveryTo
          }
        }
      `,
      variables: { data },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public estimateOrderTransfer(data: OrderTransferParams): Observable<OrderTransferEstimationResult> {
    return this.apollo
      .query<EstimateOrderTransferResponse>({
        query: gql`
          query EstimateOrderTransfer($data: OrderTransferEstimationDto!) {
            estimateOrderTransfer(data: $data) {
              success
              rejectionReason
            }
          }
        `,
        variables: { data },
        fetchPolicy: FetchPolicy.NoCache,
      })
      .pipe(map((res) => res.data.estimateOrderTransfer));
  }

  public transferOrderToRouteList(
    data: OrderTransferParams,
  ): Observable<MutationResult<TransferOrderToRouteListResponse>> {
    return this.apollo.mutate<TransferOrderToRouteListResponse>({
      mutation: gql`
        mutation TransferOrderToRouteList($data: OrderTransferEstimationDto!) {
          transferOrderToRouteList(data: $data) {
            id
          }
        }
      `,
      variables: { data },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public estimateMultipleOrderTransfer(data: TransferMultipleOrdersEstimationDto): Observable<OrderTransferResult[]> {
    return this.apollo
      .query<EstimateMultipleOrderTransferResponse>({
        query: gql`
          query EstimateMultipleOrderTransfer($data: TransferMultipleOrdersEstimationDto!) {
            estimateMultipleOrderTransfer(data: $data) {
              orderId
              success
              rejectionReason
            }
          }
        `,
        variables: { data },
        fetchPolicy: FetchPolicy.NoCache,
      })
      .pipe(map((res) => res.data.estimateMultipleOrderTransfer));
  }

  public transferMultipleOrdersToRouteList(
    data: TransferMultipleOrdersEstimationDto,
  ): Observable<MutationResult<TransferMultipleOrdersToRouteListResponse>> {
    return this.apollo.mutate<TransferMultipleOrdersToRouteListResponse>({
      mutation: gql`
        mutation TransferMultipleOrdersToRouteList($data: TransferMultipleOrdersEstimationDto!) {
          transferMultipleOrdersToRouteList(data: $data) {
            orderId
            success
            rejectionReason
          }
        }
      `,
      variables: { data },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public getListOrder(
    filter: OrderFilterDto,
    pagination: { limit: number; offset: number } = { limit: 20, offset: 0 },
  ): Observable<ApolloQueryResult<OrderListResponse>> {
    return this.apollo.query<OrderListResponse>({
      query: gql`
        query GetListOrder($filter: OrderFilterDto, $pagination: OffsetPaginationInput) {
          getListOrder(filter: $filter, pagination: $pagination) {
            total
            result {
              id
              initialOrderId
              createdAt
              recipientAddress
              canTransfer
              srcOrderId
              recipientData {
                name
                phone
              }
              zone {
                id
                name
              }
            }
          }
        }
      `,
      variables: {
        filter,
        pagination,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }

  public getRouteListWithoutRouteAndOrder(
    params: GetListParams<RouteSheetsFilter>,
  ): Observable<ApolloQueryResult<RouteSheetListGql>> {
    return this.apollo.query<RouteSheetListGql>({
      query: gql`
        query getListRouteList($filter: RouteListFilterDto, $pagination: OffsetPaginationInput) {
          getListRouteList(filter: $filter, pagination: $pagination) {
            result {
              id
              startDate
              endDate
              zones {
                id
              }
              cityId
              courier {
                id
                name
              }
            }
            total
          }
        }
      `,
      variables: {
        filter: params.filter,
        pagination: params.pagination,
      },
      fetchPolicy: FetchPolicy.NoCache,
    });
  }
}
